'use client';

import { useCallback, useEffect, useRef } from 'react';

import { scrollToSection } from '@/lib/scroll';

import type { Chapter } from '@/components/layout/Section';

type MobileMenuChapterNavigationOptions = {
  isOpen: boolean;
  onClose: () => void;
  reducedMotion: boolean;
};

/**
 * Defers chapter navigation until the menu has closed. Closing releases the
 * body scroll lock and restarts Lenis; asking the stopped instance to scroll
 * in the click handler causes mobile chapter links to be ignored.
 */
export function useMobileMenuChapterNavigation({
  isOpen,
  onClose,
  reducedMotion,
}: MobileMenuChapterNavigationOptions): (chapter: Chapter) => void {
  const pendingChapterRef = useRef<Chapter | null>(null);

  useEffect(() => {
    if (isOpen) return;

    const chapter = pendingChapterRef.current;
    if (!chapter) return;

    pendingChapterRef.current = null;
    scrollToSection(chapter, { reducedMotion });
  }, [isOpen, reducedMotion]);

  return useCallback(
    (chapter: Chapter) => {
      pendingChapterRef.current = chapter;
      onClose();
    },
    [onClose],
  );
}
